/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { ConvaiClient } from 'convai-web-sdk';
import {
  SIMULATION_TYPES,
  MESSAGE_FROM,
  MESSAGE_TYPES,
  NUDGYT_AGENT_MESSAGE_TYPES,
  VOICE_MODE_STATUS,
  VAD_STATES
} from '../constants/simulationConstants';
import {
  sanitizeMessage,
  postMessage,
  getSelectedPersonalityId,
  containsMessageNotSent,
  assembleBase64WavChunks,
  createNarrativeTemplateKeysMap,
  isIOS
} from '../helpers/utils';
import useAudioQueue from './useAudioQueue';
import useResponseTimeout from './useResponseTimeout';
import useVoiceActivityDetection from './useVoiceActivityDetection';
import useRequestUserEmotionInterval from './useRequestUserEmotionInterval';
import useRequestedUserEmotionListener from './useRequestedUserEmotionListener';
import { useSimulationContext } from '../providers/SimulationProvider';
import convaiConfig from '../config/convaiConfig';
import {
  parseConvaiEmotionResponse,
  resetConvaiEmotionState as resetConvaiOverlayEmotion
} from '../helpers/emotion';

export function useThreeConvaiClient() {
  const isInitialized = useRef(false);
  const userEmotionRef = useRef(null);
  const previousCharacterIdRef = useRef(null);
  const convaiClient = useRef(null);
  const aiTranscriptRef = useRef('');
  const facialRef = useRef([]);
  const clearResponseTimeoutRef = useRef(null);
  const handleStopResponseRef = useRef(null);
  const resetClientStateRef = useRef(null);
  const audioDataRef = useRef([]);
  const visemeDataRef = useRef([]);
  const isResponseCancelledRef = useRef(false);
  const [isAiResponseInFlight, setIsAiResponseInFlight] = useState(false);
  const prevIsAiTalkingForEmotionRef = useRef(false);

  const {
    emotion,
    userText,
    isDebugVad,
    facialData,
    userEmotion,
    isAiTalking,
    characterIds,
    simulationType,
    convaiSessionId,
    isVoiceModeActive,
    isClientInitialized,
    selectedCharacterId,
    previousLevelContext,
    isEmotionRecognitionEnabled,
    setEmotion,
    setUserText,
    setFacialData,
    setIsAiTalking,
    setConvaiSessionId,
    setIsVoiceModeActive,
    setIsClientInitialized
  } = useSimulationContext();

  const personalityId = getSelectedPersonalityId(
    characterIds,
    selectedCharacterId
  );

  // Prevents stale closure in vadFinalTranscript()
  // Ensures it always reads the current emotion value
  useEffect(() => {
    userEmotionRef.current = userEmotion;
  }, [userEmotion]);

  // Use the mute hook for audio mute/unmute functionality
  const { audioRef, enqueueAudio, clearAudioQueue } = useAudioQueue();

  useRequestedUserEmotionListener(convaiClient, isEmotionRecognitionEnabled);

  const {
    startAIListening,
    postVADState,
    isMicMutedRef,
    cancelDebouncedAudioChunk
  } = useVoiceActivityDetection({
    convaiClient,
    clearResponseTimeout: () => clearResponseTimeoutRef.current?.(),
    clearAudioQueue
  });

  /**
   * Response timeout handler
   * Detects when Convai AI fails to respond within 30 seconds
   */
  const { startResponseTimeout, clearResponseTimeout } = useResponseTimeout({
    onTimeout: () => {
      resetConvaiPlaybackState();
      setIsAiResponseInFlight(false);
      if (isVoiceModeActive) {
        postVADState(VAD_STATES.IDLE);
        startAIListening();
      }
    }
  });

  // Update the ref whenever clearResponseTimeout changes
  useEffect(() => {
    clearResponseTimeoutRef.current = clearResponseTimeout;
  }, [clearResponseTimeout]);

  /**
   * Emotion interval hook
   * Manages automatic user emotion broadcasting every 10 seconds
   */
  useRequestUserEmotionInterval({
    convaiClient,
    isVoiceModeActive,
    isClientInitialized: isInitialized.current,
    isAiTalking,
    isAiResponseInFlight
  });

  const resetConvaiPlaybackState = () => {
    clearResponseTimeout();
    clearAudioQueue();
    setIsAiTalking(false);
    setFacialData([]);
    facialRef.current = [];
    audioDataRef.current = [];
  };

  const resetClientState = () => {
    resetConvaiPlaybackState();
    resetConvaiOverlayEmotion({ setEmotion });
  };

  // Stop AI response when parent sends STOP_RESPONSE
  const handleStopResponse = () => {
    isResponseCancelledRef.current = true;
    resetConvaiPlaybackState();
    setIsAiResponseInFlight(false);
    aiTranscriptRef.current = '';
    visemeDataRef.current = [];

    convaiClient.current?.stopCharacterAudio?.();

    postMessage({
      type: MESSAGE_TYPES.CHAT,
      payload: { type: MESSAGE_TYPES.RESPONSE_ENDED }
    });
  };

  handleStopResponseRef.current = handleStopResponse;
  resetClientStateRef.current = resetConvaiPlaybackState;

  //Parent: drain audio queue when AI transcript persist fails.
  useEffect(() => {
    const onParentMessage = (event) => {
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      if (data?.type !== MESSAGE_TYPES.CHAT) return;

      const payloadType = data?.payload?.type;
      if (payloadType === NUDGYT_AGENT_MESSAGE_TYPES.STOP_RESPONSE) {
        handleStopResponseRef.current?.();
        return;
      }
      if (payloadType === NUDGYT_AGENT_MESSAGE_TYPES.STOP_AUDIO) {
        resetClientStateRef.current?.();
      }
    };

    window.addEventListener('message', onParentMessage);
    return () => window.removeEventListener('message', onParentMessage);
  }, []);

  const cleanupConvaiClient = () => {
    if (convaiClient.current) {
      try {
        // Try to disconnect/close the existing client
        if (typeof convaiClient.current.disconnect === 'function') {
          convaiClient.current.disconnect();
        }
        if (typeof convaiClient.current.close === 'function') {
          convaiClient.current.close();
        }
      } catch (error) {
        console.error('[Convai Client] Error cleaning up old client:', error);
      }
      convaiClient.current = null;
    }
  };

  const postAiResponse = (aiText) => {
    if (isResponseCancelledRef.current || !aiText) return;

    const avatarId = selectedCharacterId && selectedCharacterId?.split('_')[0];
    const characterId =
      convaiClient.current && avatarId
        ? avatarId.concat('_', convaiClient.current.characterId)
        : selectedCharacterId;

    const convaiSessionId = convaiClient.current?.sessionId ?? '';
    setConvaiSessionId(convaiSessionId);

    const payload = {
      from: MESSAGE_FROM.AI,
      characterId,
      message: sanitizeMessage(aiText),
      convaiSessionId
    };
    postMessage({ type: MESSAGE_TYPES.CHAT, payload });
    aiTranscriptRef.current = ''; // Reset for next response

    postMessage({
      type: MESSAGE_TYPES.CHAT,
      payload: { type: MESSAGE_TYPES.RESPONSE_ENDED }
    });
  };

  // Convai posts RESPONSE_ENDED before audio finishes; clear after playback ends.
  useEffect(() => {
    const wasAiTalking = prevIsAiTalkingForEmotionRef.current;
    prevIsAiTalkingForEmotionRef.current = isAiTalking;

    if (wasAiTalking && !isAiTalking && isAiResponseInFlight) {
      setIsAiResponseInFlight(false);
    }
  }, [isAiTalking, isAiResponseInFlight]);

  const convaiResponseCallback = async (response) => {
    if (!isInitialized.current) return;
    const hasAudioResponse = response.hasAudioResponse();

    if (response.hasUserQuery()) {
      const userQuery = response.getUserQuery();
      const transcript = userQuery.getTextData();
      const isFinal = userQuery.getIsFinal();
      if (transcript && isFinal) vadFinalTranscript(transcript);
    }

    if (hasAudioResponse) {
      if (isResponseCancelledRef.current) return;

      clearResponseTimeout();

      const getAudioResponse = response.getAudioResponse();
      const visemeArray = getAudioResponse?.getVisemesData()?.array || [];
      const aiText = getAudioResponse.getTextData();
      const base64audioData = getAudioResponse.getAudioData_asB64();

      // Accumulate AI text chunks
      if (aiText) aiTranscriptRef.current += aiText;

      if (base64audioData) audioDataRef.current.push(base64audioData);

      // Collect viseme data arrays
      if (visemeArray.length > 0) {
        visemeArray.forEach((frame) => {
          // Only add valid frames (skip if first value is -2, which indicates invalid frame)
          if (frame && frame[0] !== -2) {
            visemeDataRef.current.push(frame);
          }
        });
      }
      if (getAudioResponse.hasEmotionResponse()) {
        const parsed = parseConvaiEmotionResponse(
          getAudioResponse.getEmotionResponse()
        );
        if (parsed) {
          const emotion = {
            name: parsed.name.toLowerCase(),
            intensity: parsed.intensity
          };
          console.log('[Convai Emotion]:', emotion);
          setEmotion(emotion);
        }
      }

      // Handle end of AI response - assemble and enqueue
      if (getAudioResponse.getEndOfResponse()) await endOfAiResponse();
    }
  };

  const vadFinalTranscript = (transcript) => {
    if (isDebugVad) console.log('[VAD User Transcript]:', transcript);
    isResponseCancelledRef.current = false;
    isMicMutedRef.current = true;
    setIsAiResponseInFlight(true);
    startResponseTimeout();

    // Always get the latest userEmotion value from the ref
    const currentUserEmotion = userEmotionRef.current;

    postMessage({
      type: MESSAGE_TYPES.CHAT,
      payload: {
        from: MESSAGE_FROM.USER,
        message: transcript,
        ...(currentUserEmotion?.trim() && {
          userEmotion: currentUserEmotion?.toLowerCase()
        })
      }
    });
  };

  const endOfAiResponse = async () => {
    if (
      isResponseCancelledRef.current ||
      containsMessageNotSent(aiTranscriptRef.current)
    ) {
      aiTranscriptRef.current = '';
      audioDataRef.current = [];
      visemeDataRef.current = [];
      setIsAiResponseInFlight(false);
      return;
    }

    // Assemble base64 WAV chunks into a single valid WAV file
    try {
      // Properly assemble base64 WAV chunks (handles WAV headers correctly)
      const base64Audio = await assembleBase64WavChunks(audioDataRef.current);

      // Re-check cancel after async audio assembly
      if (isResponseCancelledRef.current) {
        aiTranscriptRef.current = '';
        audioDataRef.current = [];
        visemeDataRef.current = [];
        setIsAiResponseInFlight(false);
        return;
      }

      postAiResponse(aiTranscriptRef.current);
      enqueueAudio({
        audio: base64Audio,
        viseme: visemeDataRef.current
      });

      // Reset for next response
      aiTranscriptRef.current = '';
      audioDataRef.current = [];
      visemeDataRef.current = [];
    } catch (error) {
      console.error(
        '[Convai Client Error] Error converting audio to base64:',
        error
      );
      // Reset anyway to prevent memory leaks
      audioDataRef.current = [];
      visemeDataRef.current = [];
    }
  };

  const convaiErrorCallback = (type, message) => {
    console.error('[Convai Error]:', type, message);
    clearResponseTimeout();
    setIsAiResponseInFlight(false);

    // Reinitialize client if error is "STT streaming timeout"
    if (message && message.includes('STT streaming timeout')) {
      console.error(
        '[Convai Client] Reinitializing due to STT streaming timeout'
      );
      convaiClient.current.resetSession();
      postMessage({
        type: MESSAGE_TYPES.VOICE_MODE,
        error: message,
        payload: {
          status: VOICE_MODE_STATUS.INACTIVE
        }
      });

      cancelDebouncedAudioChunk();
      setIsVoiceModeActive(false);
      isInitialized.current = false;

      initializeConvaiClient();
    } else {
      postMessage({ type: MESSAGE_TYPES.CHAT, error: message });
    }
  };

  const initializeConvaiClient = () => {
    if (!personalityId || simulationType !== SIMULATION_TYPES.BASIC) {
      return;
    }

    cleanupConvaiClient();
    resetClientState();

    console.log('[Convai Client] Initializing Convai client');

    const narrativeTemplateKeysMap =
      createNarrativeTemplateKeysMap(previousLevelContext);

    convaiClient.current = new ConvaiClient({
      apiKey: convaiConfig.apiKey,
      characterId: personalityId,
      enableAudio: false,
      faceModel: 3,
      enableFacialData: true,
      ...(convaiSessionId && { sessionId: convaiSessionId }),
      ...(narrativeTemplateKeysMap.size > 0 && { narrativeTemplateKeysMap })
    });

    isInitialized.current = true;

    /**
     * Convai error callback
     * Handles SDK errors and clears response timeout
     * Reinitializes client if error is "STT streaming timeout"
     */
    convaiClient.current.setErrorCallback(convaiErrorCallback);

    /**
     * Convai response callback
     * Processes streaming audio, viseme, and emotion data from AI
     */
    convaiClient.current.setResponseCallback(convaiResponseCallback);
  };

  useEffect(() => {
    if (!isClientInitialized && simulationType === SIMULATION_TYPES.BASIC) {
      setIsClientInitialized(true);

      // If the client is initialized on iOS, we don't need to send the INITIALIZED message
      // To prevent user to send message before tapping on the screen
      if (isIOS()) return;

      postMessage({ type: MESSAGE_TYPES.INITIALIZED });
    }
  }, [isClientInitialized, simulationType]);

  useEffect(() => {
    if (
      !personalityId ||
      simulationType !== SIMULATION_TYPES.BASIC ||
      isInitialized.current
    )
      return;
    if (!isInitialized.current) initializeConvaiClient();
  }, [
    userEmotion,
    isInitialized,
    personalityId,
    simulationType,
    convaiSessionId,
    isVoiceModeActive,
    selectedCharacterId,
    previousLevelContext
  ]);

  useEffect(() => {
    if (
      simulationType !== SIMULATION_TYPES.BASIC ||
      isVoiceModeActive ||
      !convaiClient
    )
      return;

    // Send user's message to the Convai client
    if (userText) {
      isResponseCancelledRef.current = false;
      resetConvaiPlaybackState();
      setIsAiResponseInFlight(true);

      if (
        previousCharacterIdRef.current &&
        previousCharacterIdRef.current !== personalityId
      ) {
        previousCharacterIdRef.current = personalityId;
        convaiClient.current.sendTextChunk('');
        return;
      }

      previousCharacterIdRef.current = personalityId;

      // Send text to Convai and start timeout timer
      convaiClient.current.sendTextChunk(userText);
      startResponseTimeout();

      // Note: Convai does not have text receive functionality for text chat, so we send back
      // the received text message to the parent application as a confirmation
      // that the message was successfully received and processed.
      const payload = {
        from: MESSAGE_FROM.USER,
        message: userText
      };
      postMessage({ type: MESSAGE_TYPES.CHAT, payload });

      setUserText('');
    }
  }, [
    userText,
    convaiClient,
    personalityId,
    simulationType,
    isVoiceModeActive
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return resetClientState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const client = {
    convaiClient,
    isConvaiClient: true,
    isTalking: isAiTalking,
    facialData,
    emotion,
    audioRef,
    setUserText
  };

  return { client };
}

export default useThreeConvaiClient;
